import { Flex } from '@primitives/Layout'
import { fontSizes, Heading } from '@utils/Typography'
import { fathomEvent, Text, TextInput } from 'mds-web-ui'
import React, { useState } from 'react'
import styled from 'styled-components'
import { openBeaconMessage } from './beaconSupport'

const toFathomRoute = slug => slug.replace(/^\/|\/$/g, '').replace(/\//g, '-')

const trackHelpfulVote = (vote, slug) => {
  fathomEvent('support', `helpful-${vote}`, toFathomRoute(slug))
}

const VoteButton = styled('button')({
  appearance: 'none',
  border: '1px solid #E0E0E0',
  borderRadius: 999,
  background: '#fff',
  color: '#585858',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 14,
  lineHeight: 1,
  padding: '10px 16px',
  transition: 'border-color 150ms ease, background-color 150ms ease',

  '&:hover': {
    borderColor: '#BDBDBD',
    backgroundColor: '#FAFAFA'
  },

  '&[data-selected="true"]': {
    borderColor: '#585858',
    backgroundColor: '#F5F5F5'
  },

  '&:disabled': {
    cursor: 'default',
    opacity: 0.7
  }
})

export const Helpful = ({ slug, title }) => {
  const [vote, setVote] = useState(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [beaconOpened, setBeaconOpened] = useState(false)

  const handleSubmit = event => {
    event.preventDefault()

    if (!vote || submitted) {
      return
    }

    trackHelpfulVote(vote, slug)

    const trimmedComment = comment.trim()
    if (trimmedComment) {
      fathomEvent('support', 'helpful-comment', toFathomRoute(slug))
    }

    const articleUrl = `${window.location.origin}${slug}`
    const feedbackBlock = trimmedComment
      ? `\n\nFeedback:\n${trimmedComment}`
      : ''
    const opened = openBeaconMessage({
      slug,
      title,
      subject: `Docs feedback: ${title}`,
      text: `Docs article feedback\n\nArticle: ${title}\n${articleUrl}\nVote: ${vote}${feedbackBlock}`
    })

    if (typeof window !== 'undefined' && window.Beacon) {
      window.Beacon('session-data', {
        'Docs vote': vote,
        'Docs article': title,
        ...(trimmedComment && { 'Docs feedback': trimmedComment })
      })
    }

    setBeaconOpened(opened)
    setSubmitted(true)
  }

  return (
    <Flex
      as="section"
      aria-label="Was this article helpful?"
      flexDirection="column"
      gap={3}
      mt={5}
      pt={4}
      width="100%"
      css={{ borderTop: '1px solid #EDEDED' }}
    >
      <Heading as="h2" fontSize={fontSizes.body} m={0}>
        Was this helpful?
      </Heading>

      {submitted ? (
        <Text fontSize={1} color="#585859" m={0}>
          {beaconOpened
            ? 'Thanks — send your message in the window below to reach us.'
            : 'Thanks for the feedback.'}
        </Text>
      ) : (
        <Flex
          as="form"
          flexDirection="column"
          gap={3}
          width="100%"
          onSubmit={handleSubmit}
        >
          <Flex gap={2} flexWrap="wrap">
            <VoteButton
              type="button"
              data-selected={vote === 'yes'}
              onClick={() => setVote('yes')}
            >
              Yes
            </VoteButton>
            <VoteButton
              type="button"
              data-selected={vote === 'no'}
              onClick={() => setVote('no')}
            >
              No
            </VoteButton>
          </Flex>

          {vote ? (
            <Flex flexDirection="column" gap={2} width="100%">
              <Text fontSize={1} color="#585859" m={0}>
                Anything we should improve? (optional)
              </Text>
              <TextInput
                as="textarea"
                width="100%"
                minHeight={88}
                rows={4}
                value={comment}
                onChange={event => setComment(event.target.value)}
                placeholder="Tell us what was missing or unclear"
                style={{ resize: 'vertical' }}
              />
              <Flex>
                <VoteButton type="submit">Send feedback</VoteButton>
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      )}
    </Flex>
  )
}
